import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useMutation, useQuery } from "@tanstack/react-query";
import { playCompletionSound, warmUpCompletionAudio } from "@utils/completionSound";
import type { FC } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Headline, Small } from "@fronterahealth/frontera-ui-components";

import {
  FileKindEnum,
  type GetOrganizationsOutput,
  basecampOrganizationsRetrieve,
  templateGenerationPresign,
  templateGenerationStart,
  templateGenerationStatus,
} from "@api/rest/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { REPORT_TYPE_LABELS, ReportType, type ReportTypeValue } from "@pages/Templates/constants";

// ---------- Rio emotions ----------

const RioMood = {
  LOOKING: "/rio-looking.svg",
  WINKING: "/rio-winking.svg",
  HAPPY: "/rio-happy.svg",
  ADMIRING: "/rio-admiring.svg",
  SURPRISED: "/rio-surprised.svg",
} as const;
type RioMoodValue = (typeof RioMood)[keyof typeof RioMood];

const RUNNING_MOOD_CYCLE: RioMoodValue[] = [RioMood.HAPPY, RioMood.LOOKING, RioMood.WINKING, RioMood.ADMIRING];
const RUNNING_MOOD_INTERVAL_MS = 30_000;

// ---------- Polling status values returned by Temporal ----------

const WORKFLOW_STATUS = {
  RUNNING: "RUNNING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  CANCELED: "CANCELED",
  TERMINATED: "TERMINATED",
  CONTINUED_AS_NEW: "CONTINUED_AS_NEW",
  TIMED_OUT: "TIMED_OUT",
} as const;
type WorkflowStatusValue = (typeof WORKFLOW_STATUS)[keyof typeof WORKFLOW_STATUS];

const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  WORKFLOW_STATUS.COMPLETED,
  WORKFLOW_STATUS.FAILED,
  WORKFLOW_STATUS.CANCELED,
  WORKFLOW_STATUS.TERMINATED,
  WORKFLOW_STATUS.TIMED_OUT,
]);

const STATUS_POLL_INTERVAL_MS = 10_000;

// Temporal Web UI namespace + base URL for the "view in Temporal" deep link.
// Default targets the local `temporal server start-dev` web UI; override per
// env via `VITE_TEMPORAL_UI_BASE_URL` (e.g. the cloud or self-hosted URL).
const TEMPORAL_NAMESPACE = "frontera-assessment";
const TEMPORAL_UI_BASE_URL = import.meta.env.VITE_TEMPORAL_UI_BASE_URL ?? "http://localhost:8233";

function temporalWorkflowUrl(workflowId: string): string {
  return `${TEMPORAL_UI_BASE_URL}/namespaces/${TEMPORAL_NAMESPACE}/workflows/${encodeURIComponent(workflowId)}`;
}

// ---------- Searchable single-org picker ----------

interface OrgPickerProps {
  value: GetOrganizationsOutput | null;
  onChange: (org: GetOrganizationsOutput | null) => void;
  options: GetOrganizationsOutput[];
  disabled?: boolean;
}

const OrgPicker: FC<OrgPickerProps> = ({ value, onChange, options, disabled }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const close = () => {
    setOpen(false);
    setSearch("");
  };

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    if (open) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const q = search.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;
  const label = value ? value.name : "Select organization…";

  return (
    <div className="relative w-full" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
        className="flex w-full items-center justify-between rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary hover:bg-bg-default focus:outline-none focus:ring-1 focus:ring-limestone-500 disabled:opacity-60"
      >
        <span className={`truncate ${value ? "text-text-primary" : "text-text-tertiary"}`}>{label}</span>
        <span className="ml-2 text-text-tertiary">▾</span>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-72 rounded-md border border-border-subtle bg-bg-surface shadow-md">
          <div className="sticky top-0 border-b border-border-subtle bg-bg-surface p-2">
            <input
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search organizations…"
              className="w-full rounded border border-border-medium px-2 py-1 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-limestone-500"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-text-tertiary">No matches</div>
            ) : (
              filtered.map((org) => (
                <button
                  type="button"
                  key={org.id}
                  onClick={() => {
                    onChange(org);
                    close();
                  }}
                  className={`block w-full px-3 py-2 text-left text-sm hover:bg-bg-default ${
                    value?.id === org.id ? "bg-bg-default text-text-primary" : "text-text-secondary"
                  }`}
                >
                  {org.name}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- Helpers ----------

function slugifyForBuildId(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 32) || "x"
  );
}

function shortUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID().replace(/-/g, "").slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Compose a human-grep-able Temporal workflow id like
 *   `build-template-org-109-initial_assessment-a1b2c3d4`
 * The literal `org-<id>` segment makes it obvious which segment is the
 * organization identifier when scanning Temporal UI search results. The
 * backend's `_BUILD_ID_RE` enforces lowercase ascii alnum/dash/underscore
 * with a 128-char cap; this generator stays well inside that.
 */
function generateBuildId(orgId: string | number, reportType: string): string {
  const orgSlug = slugifyForBuildId(String(orgId));
  const rtSlug = slugifyForBuildId(reportType);
  return `build-template-org-${orgSlug}-${rtSlug}-${shortUuid()}`;
}

async function uploadToS3(presignedUrl: string, file: File): Promise<void> {
  const response = await fetch(presignedUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
  }
}

// ---------- Main page ----------

export const GenerateTemplate: FC = () => {
  const navigate = useNavigate();

  const [selectedOrg, setSelectedOrg] = useState<GetOrganizationsOutput | null>(null);
  const [payor, setPayor] = useState("");
  const [reportType, setReportType] = useState<ReportTypeValue>(ReportType.INITIAL_ASSESSMENT);
  const [templateName, setTemplateName] = useState("");
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [exampleFiles, setExampleFiles] = useState<File[]>([]);

  // Phase: form → running → failed (success navigates straight to the draft).
  const [phase, setPhase] = useState<"form" | "running" | "failed">("form");
  const [buildId, setBuildId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Rio mood cycling during the running phase.
  const [runningMoodIndex, setRunningMoodIndex] = useState(0);
  const [runningMoodVisible, setRunningMoodVisible] = useState(true);
  useEffect(() => {
    if (phase !== "running") return;
    let fadeTimeout: ReturnType<typeof setTimeout> | null = null;
    const interval = setInterval(() => {
      setRunningMoodVisible(false);
      fadeTimeout = setTimeout(() => {
        setRunningMoodIndex((i) => (i + 1) % RUNNING_MOOD_CYCLE.length);
        setRunningMoodVisible(true);
        fadeTimeout = null;
      }, 300);
    }, RUNNING_MOOD_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      if (fadeTimeout !== null) clearTimeout(fadeTimeout);
    };
  }, [phase]);

  // Fetch orgs for the picker.
  const { data: orgsData } = useQuery({
    queryKey: ["all-orgs"],
    queryFn: () => basecampOrganizationsRetrieve(),
  });
  // "Enterprise Admin" is a real Organization row that surfaces to
  // enterprise-admin users through this endpoint, but it's a platform
  // role bucket — not something you'd ever generate a template FOR.
  // Filter it out so the picker doesn't invite selecting it (real
  // orgs only). Case-insensitive exact match keeps the guard tight;
  // if you legitimately name an org with "Enterprise Admin" as part
  // of a larger name (e.g., "Enterprise Admin Test Clinic"), it
  // still shows.
  const allOrgs: GetOrganizationsOutput[] = (
    orgsData?.status === 200 ? (Array.isArray(orgsData.data) ? orgsData.data : [orgsData.data]) : []
  ).filter((org) => org.name.trim().toLowerCase() !== "enterprise admin");

  // Submit the form: presign every file, upload to S3, kick off workflow.
  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!selectedOrg) throw new Error("Select an organization first.");
      const newBuildId = generateBuildId(selectedOrg.id, reportType);

      const presignFile = async (file: File, kind: FileKindEnum): Promise<string> => {
        const res = await templateGenerationPresign({
          build_id: newBuildId,
          organization_id: String(selectedOrg.id),
          file_kind: kind,
          original_filename: file.name,
          mime_type: file.type || "application/octet-stream",
        });
        if (res.status !== 200) {
          throw new Error(`Failed to presign ${file.name}: ${JSON.stringify(res.data)}`);
        }
        await uploadToS3(res.data.upload_url, file);
        return res.data.s3_uri;
      };

      const templateS3Uri = templateFile ? await presignFile(templateFile, FileKindEnum.template) : null;
      const exampleS3Uris = await Promise.all(exampleFiles.map((f) => presignFile(f, FileKindEnum.example)));

      const startRes = await templateGenerationStart({
        build_id: newBuildId,
        organization_id: String(selectedOrg.id),
        payor: payor.trim() || null,
        report_type: reportType,
        template_s3_uri: templateS3Uri,
        example_s3_uris: exampleS3Uris,
        template_name: templateName.trim() || null,
      });
      if (startRes.status !== 200) {
        throw new Error(`Failed to start workflow: ${JSON.stringify(startRes.data)}`);
      }
      return newBuildId;
    },
    onSuccess: (newBuildId) => {
      setBuildId(newBuildId);
      setRunningMoodIndex(0);
      setRunningMoodVisible(true);
      setPhase("running");
    },
    onError: (e: Error) => {
      notifyError(e.message);
    },
  });

  // Poll the workflow status while we're in the "running" phase.
  const handleTerminal = useCallback(
    (statusValue: string, finalTemplateId: string | null) => {
      if (statusValue === WORKFLOW_STATUS.COMPLETED) {
        playCompletionSound();
        if (finalTemplateId) {
          void navigate(`/templates/${finalTemplateId}`);
        } else {
          // Workflow finished but no template id came back — bail to the
          // templates list so the user can find the draft manually.
          void navigate("/templates");
        }
      } else {
        setErrorMessage(`Workflow ended with status: ${statusValue}`);
        setPhase("failed");
        notifyError(`Template generation ${statusValue.toLowerCase()}.`);
      }
    },
    [navigate],
  );

  useEffect(() => {
    if (phase !== "running" || !buildId) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await templateGenerationStatus(buildId);
        if (cancelled) return;
        if (res.status !== 200) return; // 404 while Temporal catches up; keep polling
        const statusValue = res.data.execution_status as WorkflowStatusValue;
        if (TERMINAL_STATUSES.has(statusValue)) {
          handleTerminal(statusValue, res.data.template_id ?? null);
        }
      } catch {
        // Network blip — try again next tick.
      }
    };

    void poll();
    const interval = setInterval(() => void poll(), STATUS_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [phase, buildId, handleTerminal]);

  // ---------- Render branches ----------

  if (phase === "running") {
    return (
      <div className="flex flex-col min-h-full w-full items-center justify-center px-8 py-16">
        <div className="w-full max-w-2xl flex flex-col items-center text-center">
          <div className="w-full rounded-xl border border-border-subtle bg-bg-surface p-6 text-center">
            <div className="flex h-28 items-end justify-center">
              <img
                key={RUNNING_MOOD_CYCLE[runningMoodIndex]}
                src={RUNNING_MOOD_CYCLE[runningMoodIndex]}
                alt=""
                className={`size-20 animate-rio-bounce transition-opacity duration-300 ${runningMoodVisible ? "opacity-100" : "opacity-0"}`}
              />
            </div>
            <div className="mt-4">
              <Headline size="medium">Generating Template</Headline>
            </div>
            <p className="mt-16 text-base text-text-primary font-medium">Building the draft for</p>
            <div className="mt-3 flex justify-center">
              <dl className="inline-grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-lg border border-limestone-200 bg-limestone-50 px-5 py-4 text-left text-sm">
                <dt className="text-text-secondary">Organization</dt>
                <dd className="font-semibold text-text-primary">{selectedOrg?.name}</dd>
                <dt className="text-text-secondary">Report type</dt>
                <dd className="font-semibold text-text-primary">{REPORT_TYPE_LABELS[reportType]}</dd>
                {payor.trim() && (
                  <>
                    <dt className="text-text-secondary">Payor</dt>
                    <dd className="font-semibold text-text-primary">{payor.trim()}</dd>
                  </>
                )}
              </dl>
            </div>
            <p className="mt-5 text-sm text-text-secondary">
              This usually takes about 5–10 minutes.
              <br />
              You can keep this tab open or come back later — we'll play a sound when the draft is ready.
            </p>
            {buildId && (
              <div className="mt-32 flex items-center justify-center">
                <a
                  href={temporalWorkflowUrl(buildId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative w-full max-w-xs"
                >
                  <img
                    src="/temporal.png"
                    alt=""
                    aria-hidden="true"
                    className="pointer-events-none absolute bottom-full left-1/2 mb-3 h-auto w-44 -translate-x-1/2 scale-90 opacity-0 transition-all duration-200 ease-out group-hover:scale-100 group-hover:opacity-100 animate-tardigrade-idle"
                  />
                  <Button text="View in Temporal" appearance="primary" className="w-full justify-center" />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (phase === "failed") {
    return (
      <div className="flex flex-col min-h-[80vh] w-full items-center justify-center px-8 py-16">
        <div className="w-full max-w-2xl text-center">
          <img src={RioMood.SURPRISED} alt="" className="mx-auto mb-4 size-20" />
          <Headline size="medium">Generation Failed</Headline>
          <p className="mt-3 text-text-secondary">{errorMessage ?? "Something went wrong."}</p>
          <div className="mt-8 flex justify-center gap-3">
            <Button
              text="Try Again"
              appearance="primary"
              onClick={() => {
                setRunningMoodIndex(0);
                setRunningMoodVisible(true);
                setPhase("form");
                setBuildId(null);
                setErrorMessage(null);
              }}
            />
            <Button text="Back to Templates" appearance="secondary" onClick={() => void navigate("/templates")} />
          </div>
        </div>
      </div>
    );
  }

  // Form phase
  const canSubmit = !!selectedOrg && !submitMutation.isPending;

  return (
    <div className="flex flex-col min-h-full w-full max-w-6xl mx-auto px-8 md:px-16 pt-8 pb-16">
      <button
        type="button"
        onClick={() => void navigate("/templates")}
        className="mb-3 flex items-center gap-1 text-sm text-text-secondary hover:text-text-secondary"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Templates
      </button>
      <Headline size="medium">Generate Template</Headline>
      <p className="mb-6 mt-2 text-sm text-text-secondary">
        Upload a BCBA-authored instructions template and optionally a few example reports. We'll generate a draft
        template you can review and publish.
      </p>

      <div className="rounded-xl border border-border-subtle bg-bg-surface p-8 shadow-sm space-y-6">
        <div>
          <Small colorType="primary" className="mb-1 block">
            Organization
          </Small>
          <OrgPicker
            value={selectedOrg}
            onChange={setSelectedOrg}
            options={allOrgs}
            disabled={submitMutation.isPending}
          />
        </div>

        <div>
          <Small colorType="primary" className="mb-1 block">
            Payor (optional)
          </Small>
          <input
            type="text"
            value={payor}
            onChange={(e) => setPayor(e.target.value)}
            placeholder="e.g. Aetna, Medicaid, Tricare"
            disabled={submitMutation.isPending}
            className="block w-full rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-limestone-500 disabled:opacity-60"
          />
        </div>

        <div>
          <Small colorType="primary" className="mb-1 block">
            Report Type
          </Small>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value as ReportTypeValue)}
            disabled={submitMutation.isPending}
            className="block w-full rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-limestone-500 disabled:opacity-60"
          >
            {(Object.keys(REPORT_TYPE_LABELS) as ReportTypeValue[]).map((rt) => (
              <option key={rt} value={rt}>
                {REPORT_TYPE_LABELS[rt]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Small colorType="primary" className="mb-1 block">
            Template Name (optional)
          </Small>
          <p className="mb-2 text-xs text-text-tertiary">
            Leave blank to auto-name as "{selectedOrg?.name ?? "{Organization}"} {REPORT_TYPE_LABELS[reportType]}{" "}
            Template{payor.trim() ? ` - ${payor.trim()}` : ""}".
          </p>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g. Aetna IA – v2"
            maxLength={200}
            disabled={submitMutation.isPending}
            className="block w-full rounded-md border border-border-medium bg-bg-surface px-3 py-1.5 text-sm text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-limestone-500 disabled:opacity-60"
          />
        </div>

        <div>
          <Small colorType="primary" className="mb-1 block">
            Instructions Template (optional)
          </Small>
          <p className="mb-2 text-xs text-text-tertiary">
            BCBA-authored document describing section/field structure for the new template.
          </p>
          <input
            type="file"
            accept=".pdf,.docx,.doc"
            onChange={(e) => setTemplateFile(e.target.files?.[0] ?? null)}
            disabled={submitMutation.isPending}
            className="block w-full text-sm text-text-secondary file:mr-3 file:rounded-md file:border file:border-border-medium file:bg-bg-default file:px-3 file:py-1.5 file:text-sm file:text-text-primary hover:file:bg-bg-surface disabled:opacity-60"
          />
          {templateFile && <p className="mt-1 text-xs text-text-secondary">Selected: {templateFile.name}</p>}
        </div>

        <div>
          <Small colorType="primary" className="mb-1 block">
            Example Reports (optional)
          </Small>
          <p className="mb-2 text-xs text-text-tertiary">
            Optional past reports the agents can reference for boilerplate and field content.
          </p>
          <input
            type="file"
            accept=".pdf,.docx,.doc"
            multiple
            onChange={(e) => {
              if (e.target.files) {
                const incoming = Array.from(e.target.files);
                setExampleFiles((prev) => {
                  const existing = new Set(prev.map((f) => `${f.name}-${f.size}`));
                  return [...prev, ...incoming.filter((f) => !existing.has(`${f.name}-${f.size}`))];
                });
              }
              e.target.value = "";
            }}
            disabled={submitMutation.isPending}
            className="block w-full text-sm text-text-secondary file:mr-3 file:rounded-md file:border file:border-border-medium file:bg-bg-default file:px-3 file:py-1.5 file:text-sm file:text-text-primary hover:file:bg-bg-surface disabled:opacity-60"
          />
          {exampleFiles.length > 0 && (
            <ul className="mt-2 space-y-1">
              {exampleFiles.map((f, i) => (
                <li key={`${f.name}-${i}`} className="flex items-center gap-2 text-xs text-text-secondary">
                  <span className="truncate">{f.name}</span>
                  <button
                    type="button"
                    onClick={() => setExampleFiles((prev) => prev.filter((_, idx) => idx !== i))}
                    className="shrink-0 text-text-tertiary hover:text-text-error-primary"
                    aria-label={`Remove ${f.name}`}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="pt-4 mt-2 border-t border-border-subtle flex justify-end">
          <Button
            text={submitMutation.isPending ? "Starting…" : "Generate Draft"}
            appearance="primary"
            disabled={!canSubmit}
            onClick={() => {
              // Create + unlock the AudioContext NOW (this is a real user
              // gesture). Even if the workflow takes 10 minutes and the tab
              // is backgrounded by the time it finishes, the context stays
              // unlocked and the chime will play.
              warmUpCompletionAudio();
              submitMutation.mutate();
            }}
          />
        </div>
      </div>
    </div>
  );
};
